import React, { useRef, useEffect, useState } from "react";
import { X } from "lucide-react";
import FalconAIChatbot from "./FalconAIChatbot"; // adjust path as needed

const MIN_WIDTH = 300;
const MIN_HEIGHT = 400;
const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 600;
const DEFAULT_BOTTOM = 24;
const DEFAULT_RIGHT = 24;

const ChatbotPopup = ({ onClose, initialRight = DEFAULT_RIGHT, initialBottom = DEFAULT_BOTTOM }) => {
  const popupRef = useRef(null);
  const startRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDir, setResizeDir] = useState(null);

  const [size, setSize] = useState({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
  const [pos, setPos] = useState({ left: 0, top: 0 });

  useEffect(() => {
    const computeInitial = () => {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const left = Math.max(16, vw - initialRight - DEFAULT_WIDTH);
      const top = Math.max(16, vh - initialBottom - DEFAULT_HEIGHT);
      setPos({ left, top });
      setSize({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
    };
    computeInitial();
    const onResize = () => computeInitial();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [initialRight, initialBottom]);

  const handleResizeStart = (e, direction) => {
    e.preventDefault();
    e.stopPropagation();
    
    const rect = popupRef.current?.getBoundingClientRect();
    
    startRef.current = {
      mouseX: e.clientX,
      mouseY: e.clientY,
      width: size.width,
      height: size.height,
      left: rect?.left || pos.left,
      top: rect?.top || pos.top,
    };
    setResizeDir(direction);
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return;

    const handleMouseMove = (e) => {
      if (!startRef.current || !resizeDir) return;

      const dx = e.clientX - startRef.current.mouseX;
      const dy = e.clientY - startRef.current.mouseY;

      let newWidth = startRef.current.width;
      let newHeight = startRef.current.height;
      let newLeft = startRef.current.left;
      let newTop = startRef.current.top;

      // Horizontal adjustments
      if (resizeDir.includes("right")) {
        newWidth = Math.max(MIN_WIDTH, startRef.current.width + dx);
      }
      if (resizeDir.includes("left")) {
        const proposedWidth = startRef.current.width - dx;
        if (proposedWidth >= MIN_WIDTH) {
          newWidth = proposedWidth;
          newLeft = startRef.current.left + dx;
        } else {
          newWidth = MIN_WIDTH;
          newLeft = startRef.current.left + startRef.current.width - MIN_WIDTH;
        }
      }

      // Vertical adjustments
      if (resizeDir.includes("bottom")) {
        newHeight = Math.max(MIN_HEIGHT, startRef.current.height + dy);
      }
      if (resizeDir.includes("top")) {
        const proposedHeight = startRef.current.height - dy;
        if (proposedHeight >= MIN_HEIGHT) {
          newHeight = proposedHeight;
          newTop = startRef.current.top + dy;
        } else {
          newHeight = MIN_HEIGHT;
          newTop = startRef.current.top + startRef.current.height - MIN_HEIGHT;
        }
      }

      // Constrain within viewport
      const vw = window.innerWidth;
      const vh = window.innerHeight;

      if (newLeft < 8) {
        if (resizeDir.includes("left")) {
          newWidth = newWidth + newLeft - 8;
        }
        newLeft = 8;
      }
      if (newTop < 8) {
        if (resizeDir.includes("top")) {
          newHeight = newHeight + newTop - 8;
        }
        newTop = 8;
      }

      const maxWidth = vw - newLeft - 8;
      const maxHeight = vh - newTop - 8;
      
      if (newWidth > maxWidth) newWidth = maxWidth;
      if (newHeight > maxHeight) newHeight = maxHeight;

      setSize({ width: Math.round(newWidth), height: Math.round(newHeight) });
      setPos({ left: Math.round(newLeft), top: Math.round(newTop) });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDir(null);
      startRef.current = null;
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing, resizeDir]);

  const dragging = useRef(false);
  const dragStart = useRef(null);

  const handleDragStart = (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    dragging.current = true;
    dragStart.current = { mouseX: e.clientX, mouseY: e.clientY, left: pos.left, top: pos.top };
    document.addEventListener("mousemove", handleDragging);
    document.addEventListener("mouseup", handleDragEnd);
  };

  const handleDragging = (e) => {
    if (!dragging.current || !dragStart.current) return;
    const dx = e.clientX - dragStart.current.mouseX;
    const dy = e.clientY - dragStart.current.mouseY;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let newLeft = dragStart.current.left + dx;
    let newTop = dragStart.current.top + dy;
    newLeft = Math.min(Math.max(8, newLeft), Math.max(8, vw - size.width - 8));
    newTop = Math.min(Math.max(8, newTop), Math.max(8, vh - size.height - 8));
    setPos({ left: newLeft, top: newTop });
  };

  const handleDragEnd = () => {
    dragging.current = false;
    dragStart.current = null;
    document.removeEventListener("mousemove", handleDragging);
    document.removeEventListener("mouseup", handleDragEnd);
  };

  return (
    <>
      <div
        ref={popupRef}
        className="fixed bg-white shadow-2xl rounded-lg overflow-hidden border-2 border-gray-200"
        style={{
          width: `${size.width}px`,
          height: `${size.height}px`,
          left: `${pos.left}px`,
          top: `${pos.top}px`,
          minWidth: `${MIN_WIDTH}px`,
          minHeight: `${MIN_HEIGHT}px`,
          maxWidth: `calc(100vw - 16px)`,
          maxHeight: `calc(100vh - 16px)`,
          transition: isResizing ? 'none' : 'box-shadow 0.2s',
          zIndex: 9999,
        }}
      >
        {/* Enhanced resize handles with better hit areas and visual feedback */}
        
        {/* Edge handles - with hover effects */}
        <div
          className="absolute top-0 left-0 right-0 h-1 cursor-n-resize hover:bg-blue-400 hover:h-1.5 transition-all"
          onMouseDown={(e) => handleResizeStart(e, "top")}
          style={{ zIndex: 60 }}
          title="Resize from top"
        />
        <div
          className="absolute bottom-0 left-0 right-0 h-1 cursor-s-resize hover:bg-blue-400 hover:h-1.5 transition-all"
          onMouseDown={(e) => handleResizeStart(e, "bottom")}
          style={{ zIndex: 60 }}
          title="Resize from bottom"
        />
        <div
          className="absolute top-0 bottom-0 left-0 w-1 cursor-w-resize hover:bg-blue-400 hover:w-1.5 transition-all"
          onMouseDown={(e) => handleResizeStart(e, "left")}
          style={{ zIndex: 60 }}
          title="Resize from left"
        />
        <div
          className="absolute top-0 bottom-0 right-0 w-1 cursor-e-resize hover:bg-blue-400 hover:w-1.5 transition-all"
          onMouseDown={(e) => handleResizeStart(e, "right")}
          style={{ zIndex: 60 }}
          title="Resize from right"
        />

        {/* Corner handles - larger and more visible */}
        <div
          className="absolute top-0 left-0 w-3 h-3 cursor-nw-resize hover:bg-blue-500 rounded-tl-lg transition-colors"
          onMouseDown={(e) => handleResizeStart(e, "top-left")}
          style={{ zIndex: 61 }}
          title="Resize from top-left"
        />
        <div
          className="absolute top-0 right-0 w-3 h-3 cursor-ne-resize hover:bg-blue-500 rounded-tr-lg transition-colors"
          onMouseDown={(e) => handleResizeStart(e, "top-right")}
          style={{ zIndex: 61 }}
          title="Resize from top-right"
        />
        <div
          className="absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize hover:bg-blue-500 rounded-bl-lg transition-colors"
          onMouseDown={(e) => handleResizeStart(e, "bottom-left")}
          style={{ zIndex: 61 }}
          title="Resize from bottom-left"
        />
        <div
          className="absolute bottom-0 right-0 w-3 h-3 cursor-se-resize hover:bg-blue-500 rounded-br-lg transition-colors"
          onMouseDown={(e) => handleResizeStart(e, "bottom-right")}
          style={{ zIndex: 61 }}
          title="Resize from bottom-right"
        />

        {/* Header (draggable) with improved styling */}
        <div
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between cursor-move shadow-md"
          onMouseDown={handleDragStart}
          style={{ userSelect: "none", zIndex: 50 }}
        >
          <h3 className="font-semibold text-base">AI Assistant</h3>
          <button
            onClick={() => {
              setSize({ width: DEFAULT_WIDTH, height: DEFAULT_HEIGHT });
              const vw = window.innerWidth;
              const vh = window.innerHeight;
              setPos({
                left: Math.max(16, vw - DEFAULT_RIGHT - DEFAULT_WIDTH),
                top: Math.max(16, vh - DEFAULT_BOTTOM - DEFAULT_HEIGHT),
              });
              if (typeof onClose === "function") onClose();
            }}
            className="hover:bg-blue-800 p-1.5 rounded transition-colors"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Chatbot content */}
        <div style={{ height: `calc(100% - 48px)` }}>
          <FalconAIChatbot />
        </div>
      </div>

      {/* Overlay during resize with cursor feedback */}
      {isResizing && (
        <div 
          className="fixed inset-0" 
          style={{ 
            cursor: `${resizeDir}-resize`,
            backgroundColor: 'rgba(0, 0, 0, 0.01)',
            zIndex: 9998,
          }} 
        />
      )}
    </>
  );
};

export default ChatbotPopup;
